import { Injectable } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { AppRole, AppRoleType } from './roles.constants';
import { PermissionType } from './permissions.constants';
import { ROLE_PERMISSIONS } from './role-permissions';
import { getPermissionsForRoute } from './menu-permissions.config';

/**
 * Centralized RBAC service. Use this instead of scattered role checks.
 * Roles are derived from auth userTypes (frontDesk -> FRONT_DESK, etc.).
 * Administrator (isAdministrator / roleName ADMINISTRATOR) gets all permissions.
 */
@Injectable({
  providedIn: 'root',
})
export class RbacService {
  constructor(private auth: AuthService) {}

  /** Get app roles for current user from userTypes. Administrator is included when isAdministrator. */
  getEffectiveRoles(): AppRoleType[] {
    const user = this.auth.getCurrentUser();
    if (!user) return [];

    const roles: AppRoleType[] = [];
    // Legacy fallback: derive roles from userTypes
    if (user.userTypes?.frontDesk) roles.push(AppRole.FRONT_DESK);
    if (user.userTypes?.nurse) roles.push(AppRole.NURSE);
    if (user.userTypes?.doctor) roles.push(AppRole.DOCTOR);
    if (user.userTypes?.biller) roles.push(AppRole.BILLER);

    // Priority mapping: derive roles from roles[].roleName as well (e.g. "ISA BILLER")
    // This is necessary because some login responses may not set userTypes.biller=true.
    if (Array.isArray(user.roles)) {
      const normalizedRoleNames = user.roles
        .map((r) => String(r?.roleName || '').trim().toUpperCase().replace(/\s+/g, ' '))
        .filter(Boolean);

      if (normalizedRoleNames.includes('ISA BILLER') || normalizedRoleNames.includes('BILLER')) {
        roles.push(AppRole.BILLER);
      }
      if (normalizedRoleNames.includes('FRONTDESK') || normalizedRoleNames.includes('FRONT DESK')) {
        roles.push(AppRole.FRONT_DESK);
      }
      if (normalizedRoleNames.includes('DOCTOR')) {
        roles.push(AppRole.DOCTOR);
      }
      if (normalizedRoleNames.includes('NURSE')) {
        roles.push(AppRole.NURSE);
      }
    }

    const isAdmin =
      user.isAdministrator === true ||
      (Array.isArray(user.roles) &&
        user.roles.some(
          (r) =>
            String(r.roleName || '').toUpperCase() === 'ADMINISTRATOR'
        ));
    if (isAdmin) roles.push(AppRole.ADMINISTRATOR);

    return roles;
  }

  /** Whether current user has the given role. */
  hasRole(role: AppRoleType): boolean {
    return this.getEffectiveRoles().includes(role);
  }

  /** Whether current user has any of the given roles. */
  hasAnyRole(roles: AppRoleType[]): boolean {
    const effective = this.getEffectiveRoles();
    return roles.some((r) => effective.includes(r));
  }

  /** Whether current user is administrator (full access). */
  isAdministrator(): boolean {
    return this.hasRole(AppRole.ADMINISTRATOR);
  }

  /** Set of permission keys the current user has (from all their roles). Administrator gets all. */
  getPermissions(): Set<PermissionType> {
    const roles = this.getEffectiveRoles();
    const set = new Set<PermissionType>();

    if (roles.includes(AppRole.ADMINISTRATOR)) {
      // Admin: grant all known permissions from ROLE_PERMISSIONS
      (Object.keys(ROLE_PERMISSIONS) as AppRoleType[]).forEach((role) => {
        ROLE_PERMISSIONS[role].forEach((p) => set.add(p));
      });
      set.add('master.any' as PermissionType);
      set.add('batch.any' as PermissionType);
      set.add('daily_roster.view' as PermissionType);
      set.add('follow_up.view' as PermissionType);
      return set;
    }

    roles.forEach((role) => {
      const perms = ROLE_PERMISSIONS[role];
      if (perms) perms.forEach((p) => set.add(p));
    });
    return set;
  }

  /** Whether current user has the given permission. */
  can(permission: PermissionType): boolean {
    return this.getPermissions().has(permission);
  }

  /** Whether current user has any of the given permissions. */
  canAny(permissions: PermissionType[]): boolean {
    const set = this.getPermissions();
    return permissions.some((p) => set.has(p));
  }

  /** Whether current user has all of the given permissions. */
  canAll(permissions: PermissionType[]): boolean {
    const set = this.getPermissions();
    return permissions.every((p) => set.has(p));
  }

  /**
   * Check visibility for menu/UI when rule is permission-based.
   * If allowedPermissions is empty or undefined, visible to all authenticated users.
   */
  canSeeMenu(allowedPermissions: PermissionType[] | undefined): boolean {
    if (!this.auth.isAuthenticated()) return false;
    if (!allowedPermissions || allowedPermissions.length === 0) return true;
    return this.canAny(allowedPermissions);
  }

  /**
   * Check if current user can access the given route path.
   * Uses centralized route-to-permission mapping.
   */
  canAccessRoute(routePath: string): boolean {
    const perms = getPermissionsForRoute(routePath);
    return perms.length === 0 || this.canAny(perms);
  }
}
